import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  TrendingUp,
  Shield,
  Zap,
  Mail,
  TrendingDown,
  Target,
  Briefcase,
  Clock,
  Search,
  Users,
  BarChart3,
  Lightbulb,
  FileText,
  CheckCircle2,
  Star,
  ArrowRight,
  Camera,
} from "lucide-react";
import lifactorLogo from "@/assets/lifactor-logo-black.svg";
import auditScreenshot1 from "@/assets/audit-screenshot-1.png";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguageWithOverride, Language } from "@/hooks/use-browser-language";
import { getLandingTranslation } from "@/i18n/social-selling-landing";

const SocialSellingLanding = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguageWithOverride();

  const t = (key: Parameters<typeof getLandingTranslation>[1]) => 
    getLandingTranslation(language, key);

  const toggleLanguage = () => {
    const newLang: Language = language === "pl" ? "en" : "pl";
    setLanguage(newLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", newLang);
    window.history.replaceState({}, "", url.toString());
  };

  const handleCTA = () => {
    navigate(`/raport-socialselling?lang=${language}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 sm:px-6 lg:px-8">
        {/* Logo Header */}
        <div className="container max-w-7xl pt-8 pb-4">
          <img src={lifactorLogo} alt="Lifactor" className="h-10 md:h-12" />
        </div>
        
        {/* Hero Content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="container max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="flex flex-wrap gap-2">
                <Badge variant="default" className="text-sm bg-primary/90">
                  {t("badgeBeta")}
                </Badge>
                <Badge variant="secondary" className="text-sm">
                  {t("badgeCompetitors")}
                </Badge>
                <Badge variant="secondary" className="text-sm">
                  {t("badgeMetrics")}
                </Badge>
                <Badge variant="secondary" className="text-sm">
                  {t("badgeReport")}
                </Badge>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                {t("heroTitle1")}
                <br />
                <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  {t("heroTitle2")}
                </span>
                <br />
                <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  {t("heroTitle3")}
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-muted-foreground">
                {t("heroDescription")} <span className="font-semibold text-primary">{t("heroBetaNote")}</span>
              </p>

              <div className="space-y-4">
                <Button
                  onClick={handleCTA}
                  size="lg"
                  className="text-lg px-8 py-6 shadow-elegant hover:shadow-glow transition-smooth animate-pulse"
                >
                  {t("heroCta")}
                  <ArrowRight className="ml-2" />
                </Button>

                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-primary" />
                    {t("heroFeature1")}
                  </span>
                  <span className="flex items-center gap-1">
                    <Shield className="w-4 h-4 text-primary" />
                    {t("heroFeature2")}
                  </span>
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4 text-primary" />
                    {t("heroFeature3")}
                  </span>
                </div>
              </div>

              <div className="pt-8">
                <p className="text-sm text-muted-foreground mb-2">
                  {t("heroTrust")}
                </p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-xs font-bold"
                    >
                      {i}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hidden lg:block animate-fade-in">
              <div className="relative max-w-2xl mx-auto">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-3xl"></div>
                
                {/* Screenshot */}
                <div className="relative rounded-xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] hover:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.4)] transition-all duration-500 hover:-translate-y-2">
                  <img 
                    src={auditScreenshot1} 
                    alt="Social Selling Audit Report - Complete Analysis" 
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-background px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("problemTitle")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              {
                icon: TrendingDown,
                title: t("problem1Title"),
                description: t("problem1Desc"),
              },
              {
                icon: Target,
                title: t("problem2Title"),
                description: t("problem2Desc"),
              },
              {
                icon: Briefcase,
                title: t("problem3Title"),
                description: t("problem3Desc"),
              },
              {
                icon: Clock,
                title: t("problem4Title"),
                description: t("problem4Desc"),
              },
            ].map((problem, idx) => (
              <Card
                key={idx}
                className="hover:scale-105 transition-transform duration-300 hover:shadow-elegant"
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-destructive/10">
                      <problem.icon className="w-6 h-6 text-destructive" />
                    </div>
                    <div>
                      <CardTitle className="text-xl mb-2">{problem.title}</CardTitle>
                      <p className="text-muted-foreground">{problem.description}</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="text-center p-6 bg-muted/50 rounded-lg">
            <p className="text-lg md:text-xl font-semibold">
              <span className="text-4xl font-bold text-primary">78%</span> {t("problemStat")}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t("problemSource")}
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-gradient-subtle px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="text-center mb-16 space-y-4 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("solutionTitle")}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t("solutionSubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: t("card1Title"),
                description: t("card1Desc"),
              },
              {
                icon: Search,
                title: t("card2Title"),
                description: t("card2Desc"),
              },
              {
                icon: TrendingUp,
                title: t("card3Title"),
                description: t("card3Desc"),
              },
              {
                icon: BarChart3,
                title: t("card4Title"),
                description: t("card4Desc"),
              },
              {
                icon: Camera,
                title: t("card5Title"),
                description: t("card5Desc"),
              },
              {
                icon: Users,
                title: t("card6Title"),
                description: t("card6Desc"),
              },
              {
                icon: FileText,
                title: t("card7Title"),
                description: t("card7Desc"),
              },
              {
                icon: CheckCircle2,
                title: t("card8Title"),
                description: t("card8Desc"),
              },
            ].map((benefit, idx) => (
              <Card
                key={idx}
                className="hover:scale-105 transition-transform duration-300 border-2 hover:border-primary/50"
              >
                <CardHeader>
                  <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                    <benefit.icon className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl mb-2">{benefit.title}</CardTitle>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardHeader>
              </Card>
            ))}

            {/* 9th Card - CTA */}
            <Card
              className="hover:scale-105 transition-transform duration-300 border-2 border-primary bg-gradient-to-br from-primary/5 to-primary/10 cursor-pointer group"
              onClick={handleCTA}
            >
              <CardHeader className="h-full flex flex-col justify-center items-center text-center">
                <div className="p-4 rounded-full bg-primary/20 w-fit mb-4 group-hover:bg-primary/30 transition-colors">
                  <ArrowRight className="w-10 h-10 text-primary" />
                </div>
                <CardTitle className="text-2xl mb-3 text-primary">{t("ctaCardTitle")}</CardTitle>
                <p className="text-muted-foreground mb-4">
                  {t("ctaCardDesc")}
                </p>
                <Button size="lg" className="shadow-elegant hover:shadow-glow transition-smooth">
                  {t("ctaCardButton")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-background px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("howTitle")}
            </h2>
            <p className="text-xl text-muted-foreground">{t("howSubtitle")}</p>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 max-w-5xl mx-auto">
            {[
              {
                number: 1,
                icon: FileText,
                title: t("step1Title"),
                time: t("step1Time"),
                description: t("step1Desc"),
                subtitle: t("step1Sub"),
              },
              {
                number: 2,
                icon: Search,
                title: t("step2Title"),
                time: t("step2Time"),
                description: t("step2Desc"),
                subtitle: t("step2Sub"),
              },
              {
                number: 3,
                icon: Mail,
                title: t("step3Title"),
                time: t("step3Time"),
                description: t("step3Desc"),
                subtitle: t("step3Sub"),
              },
            ].map((step, idx) => (
              <div key={idx} className="flex-1 text-center relative">
                <div className="mb-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-bold mb-4 shadow-elegant">
                    {step.number}
                  </div>
                  <div className="p-4 rounded-lg bg-primary/10 w-fit mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-2">{step.title}</h3>
                <Badge variant="secondary" className="mb-4">
                  {step.time}
                </Badge>
                <p className="text-muted-foreground mb-2">{step.description}</p>
                <p className="text-sm text-muted-foreground">{step.subtitle}</p>
                {idx < 2 && (
                  <ArrowRight className="hidden lg:block absolute top-10 -right-8 w-6 h-6 text-primary" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Preview Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-gradient-subtle px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("reportTitle")}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Card className="shadow-elegant hover:shadow-glow transition-smooth transform hover:rotate-1">
                <CardHeader>
                  <CardTitle>Social Selling Audit Report</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                      <span className="font-semibold">Overall Score</span>
                      <span className="text-2xl font-bold text-primary">8.5/10</span>
                    </div>
                    <div className="space-y-2">
                      {["Personal Branding", "Content Quality", "Engagement Rate"].map(
                        (metric) => (
                          <div key={metric} className="flex justify-between items-center">
                            <span className="text-sm">{metric}</span>
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className="w-4 h-4 fill-primary text-primary"
                                />
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-3">Top Recommendations</h4>
                    <ul className="space-y-2">
                      {["Optimize LinkedIn headlines", "Increase posting frequency", "Engage with industry leaders"].map(
                        (rec) => (
                          <li key={rec} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>{rec}</span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4 order-1 lg:order-2">
              {[
                t("reportItem1"),
                t("reportItem2"),
                t("reportItem3"),
                t("reportItem4"),
                t("reportItem5"),
                t("reportItem6"),
                t("reportItem7"),
                t("reportItem8"),
                t("reportItem9"),
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-lg">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-background px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("socialProofTitle")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: t("testimonial1Quote"),
                author: "Jan Kowalski",
                role: t("testimonial1Role"),
                company: "TechCorp",
              },
              {
                quote: t("testimonial2Quote"),
                author: "Anna Nowak",
                role: t("testimonial2Role"),
                company: "MarketPro",
              },
              {
                quote: t("testimonial3Quote"),
                author: "Piotr Wiśniewski",
                role: t("testimonial3Role"),
                company: "SalesMasters",
              },
            ].map((testimonial, idx) => (
              <Card key={idx} className="hover:shadow-elegant transition-smooth">
                <CardHeader>
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-5 h-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground italic mb-4">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center font-bold">
                      {testimonial.author.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <p className="font-semibold">{testimonial.author}</p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-gradient-subtle px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("statsTitle")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: "78%",
                description: t("stat1Desc"),
              },
              {
                number: "51%",
                description: t("stat2Desc"),
              },
              {
                number: "45%",
                description: t("stat3Desc"),
              },
              {
                number: "5.3x",
                description: t("stat4Desc"),
              },
            ].map((stat, idx) => (
              <Card
                key={idx}
                className="text-center hover:scale-105 transition-transform duration-300"
              >
                <CardHeader>
                  <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent mb-4">
                    {stat.number}
                  </div>
                  <p className="text-muted-foreground">{stat.description}</p>
                </CardHeader>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            {t("statsSource")}
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-background px-4 sm:px-6 lg:px-8">
        <div className="container max-w-4xl">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {t("faqTitle")}
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { question: t("faq1Q"), answer: t("faq1A") },
              { question: t("faq2Q"), answer: t("faq2A") },
              { question: t("faq3Q"), answer: t("faq3A") },
              { question: t("faq4Q"), answer: t("faq4A") },
              { question: t("faq5Q"), answer: t("faq5A") },
              { question: t("faq6Q"), answer: t("faq6A") },
              { question: t("faq7Q"), answer: t("faq7A") },
              { question: t("faq8Q"), answer: t("faq8A") },
              { question: t("faq9Q"), answer: t("faq9A") },
              { question: t("faq10Q"), answer: t("faq10A") },
            ].map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`}>
                <AccordionTrigger className="text-left text-lg font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-r from-primary to-primary/80 px-4 sm:px-6 lg:px-8">
        <div className="container max-w-4xl text-center">
          <div className="space-y-8">
            <Badge variant="secondary" className="text-sm px-4 py-2">
              {t("badgeBeta")}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-primary-foreground">
              {t("finalCtaTitle")}
            </h2>
            <p className="text-xl text-primary-foreground/90">
              {t("finalCtaSubtitle")}
            </p>
            <Button
              onClick={handleCTA}
              size="lg"
              variant="secondary"
              className="text-lg px-8 py-6 shadow-elegant hover:shadow-glow transition-smooth"
            >
              {t("finalCtaButton")}
              <ArrowRight className="ml-2" />
            </Button>
            <div className="flex flex-wrap justify-center gap-6 text-primary-foreground/90 pt-4">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {t("finalFeature1")}
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {t("finalFeature2")}
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {t("finalFeature3")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-background border-t px-4 sm:px-6 lg:px-8">
        <div className="container max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {t("footerCopyright")}
            </div>
            <div className="flex items-center gap-6">
              <a 
                href="/polityka-prywatnosci" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t("footerPrivacy")}
              </a>
              <a 
                href="/regulamin" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t("footerTerms")}
              </a>
              <LanguageSwitcher language={language} onToggle={toggleLanguage} className="border-none pt-0" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SocialSellingLanding;
